import { useEffect, useMemo, useState, type ChangeEvent, type FormEvent } from "react";
import Modal from "@/components/base/Modal";
import { Field, Input, Select, Textarea } from "@/components/base/FormControls";
import { useAgenceData } from "@/pages/agence/data/AgenceDataProvider";
import {
  AGENCE_BIEN_TYPE_LABELS,
  AGENCE_DEPENSE_CATEGORIE_OPTIONS,
  type AgenceDepense,
  type AgenceDepenseCategorie,
  type AgenceDepenseInput,
} from "@/pages/agence/data/types";
import { useProprietaires } from "@/pages/agence/proprietaires/ProprietairesProvider";
import { proprietaireNomComplet } from "@/pages/agence/proprietaires/types";

interface DepenseFormProps {
  open: boolean;
  onClose: () => void;
  defaultProprietaireId?: string;
  defaultImmeubleId?: string;
  defaultBienId?: string;
  initial?: AgenceDepense;
}

interface FormState {
  proprietaireId: string;
  immeubleId: string;
  bienId: string;
  libelle: string;
  categorie: AgenceDepenseCategorie;
  montant: string;
  date: string;
  description: string;
  justificatif: string;
}

const EMPTY_STATE: FormState = {
  proprietaireId: "",
  immeubleId: "",
  bienId: "",
  libelle: "",
  categorie: "reparation",
  montant: "",
  date: "",
  description: "",
  justificatif: "",
};

export default function DepenseForm({
  open,
  onClose,
  defaultProprietaireId,
  defaultImmeubleId,
  defaultBienId,
  initial,
}: DepenseFormProps) {
  const { immeubles, biens, getBien, addDepense, updateDepense } = useAgenceData();
  const { proprietaires } = useProprietaires();

  const [form, setForm] = useState<FormState>(EMPTY_STATE);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        proprietaireId: initial.proprietaireId,
        immeubleId: initial.immeubleId,
        bienId: initial.bienId,
        libelle: initial.libelle,
        categorie: initial.categorie,
        montant: String(initial.montant),
        date: initial.date,
        description: initial.description,
        justificatif: initial.justificatif,
      });
      setErrors({});
      return;
    }
    setForm(() => {
      const base: FormState = { ...EMPTY_STATE, date: new Date().toISOString().slice(0, 10) };
      if (defaultBienId) {
        const bien = getBien(defaultBienId);
        if (bien) {
          base.bienId = bien.id;
          base.immeubleId = bien.immeubleId;
          base.proprietaireId = bien.proprietaireId;
        }
      }
      if (defaultImmeubleId && !base.immeubleId) {
        base.immeubleId = defaultImmeubleId;
        const immeuble = immeubles.find((item) => item.id === defaultImmeubleId);
        if (immeuble) base.proprietaireId = immeuble.proprietaireId;
      }
      if (defaultProprietaireId && !base.proprietaireId) base.proprietaireId = defaultProprietaireId;
      return base;
    });
    setErrors({});
  }, [open, defaultProprietaireId, defaultImmeubleId, defaultBienId, getBien, immeubles]);

  const proprietaireOptions = useMemo(
    () => proprietaires.map((item) => ({ value: item.id, label: proprietaireNomComplet(item) })),
    [proprietaires]
  );

  const immeubleOptions = useMemo(
    () =>
      immeubles
        .filter((immeuble) => !form.proprietaireId || immeuble.proprietaireId === form.proprietaireId)
        .map((immeuble) => ({ value: immeuble.id, label: immeuble.nom })),
    [immeubles, form.proprietaireId]
  );

  const bienOptions = useMemo(
    () =>
      biens
        .filter((bien) => !form.immeubleId || bien.immeubleId === form.immeubleId)
        .map((bien) => ({
          value: bien.id,
          label: `${AGENCE_BIEN_TYPE_LABELS[bien.type]}${bien.reference ? ` · ${bien.reference}` : ""}`,
        })),
    [biens, form.immeubleId]
  );

  const update = (key: keyof FormState, value: string) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value } as FormState;
      if (key === "proprietaireId") {
        next.immeubleId = "";
        next.bienId = "";
      }
      if (key === "immeubleId") {
        next.bienId = "";
        const immeuble = immeubles.find((item) => item.id === value);
        if (immeuble) next.proprietaireId = immeuble.proprietaireId;
      }
      if (key === "bienId") {
        const bien = getBien(value);
        if (bien) {
          next.immeubleId = bien.immeubleId;
          next.proprietaireId = bien.proprietaireId;
        }
      }
      return next;
    });
  };

  const handleJustificatif = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    update("justificatif", file ? file.name : "");
  };

  const resetAndClose = () => {
    setForm(EMPTY_STATE);
    setErrors({});
    onClose();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextErrors: Record<string, string> = {};
    if (!form.libelle.trim()) nextErrors.libelle = "Indiquez un libellé.";
    if (!form.montant.trim() || Number(form.montant) <= 0) nextErrors.montant = "Indiquez un montant valide.";
    if (!form.date) nextErrors.date = "Indiquez la date de la dépense.";
    if (!form.immeubleId && !form.bienId) nextErrors.cible = "Rattachez la dépense à un immeuble ou à un bien.";

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const input: AgenceDepenseInput = {
      proprietaireId: form.proprietaireId,
      immeubleId: form.immeubleId,
      bienId: form.bienId,
      libelle: form.libelle.trim(),
      categorie: form.categorie,
      montant: Math.max(0, Number(form.montant) || 0),
      date: form.date,
      description: form.description.trim(),
      justificatif: form.justificatif,
    };

    if (initial) {
      updateDepense(initial.id, input);
    } else {
      addDepense(input);
    }
    resetAndClose();
  };

  return (
    <Modal
      open={open}
      onClose={resetAndClose}
      size="lg"
      title={initial ? "Modifier la dépense" : "Ajouter une dépense"}
      description="Enregistrez une charge liée à un immeuble ou un bien d'un propriétaire géré. Libellé, catégorie, montant, date et rattachement sont obligatoires."
      footer={
        <>
          <button
            type="button"
            onClick={resetAndClose}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
          >
            Annuler
          </button>
          <button
            type="submit"
            form="agence-depense-form"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-check-line text-base" aria-hidden="true" />
            Enregistrer la dépense
          </button>
        </>
      }
    >
      <form id="agence-depense-form" onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-x-4 gap-y-3.5 sm:grid-cols-2">
          <Field label="Libellé" htmlFor="agence-dep-libelle" required error={errors.libelle} className="sm:col-span-2">
            <Input
              id="agence-dep-libelle"
              value={form.libelle}
              onChange={(event) => update("libelle", event.target.value)}
              placeholder="Ex. Réparation plomberie immeuble A"
              maxLength={120}
              invalid={Boolean(errors.libelle)}
            />
          </Field>

          <Field label="Catégorie" htmlFor="agence-dep-categorie" required>
            <Select
              id="agence-dep-categorie"
              value={form.categorie}
              onChange={(event) => update("categorie", event.target.value)}
              options={AGENCE_DEPENSE_CATEGORIE_OPTIONS}
            />
          </Field>

          <Field label="Montant (FCFA)" htmlFor="agence-dep-montant" required error={errors.montant}>
            <Input
              id="agence-dep-montant"
              type="number"
              min={0}
              max={100000000}
              value={form.montant}
              onChange={(event) => update("montant", event.target.value)}
              placeholder="Ex. 25000"
              invalid={Boolean(errors.montant)}
            />
          </Field>

          <Field label="Date" htmlFor="agence-dep-date" required error={errors.date} className="sm:col-span-2">
            <Input
              id="agence-dep-date"
              type="date"
              value={form.date}
              onChange={(event) => update("date", event.target.value)}
              invalid={Boolean(errors.date)}
            />
          </Field>

          <Field label="Propriétaire" htmlFor="agence-dep-proprietaire" hint="Facultatif, filtre les immeubles.">
            <Select
              id="agence-dep-proprietaire"
              value={form.proprietaireId}
              onChange={(event) => update("proprietaireId", event.target.value)}
              options={proprietaireOptions}
              placeholder={proprietaires.length > 0 ? "Aucun propriétaire en particulier" : "Aucun propriétaire créé"}
              disabled={proprietaires.length === 0}
            />
          </Field>

          <Field label="Immeuble" htmlFor="agence-dep-immeuble" hint="Rattachez un immeuble ou un bien.">
            <Select
              id="agence-dep-immeuble"
              value={form.immeubleId}
              onChange={(event) => update("immeubleId", event.target.value)}
              options={immeubleOptions}
              placeholder={immeubleOptions.length > 0 ? "Aucun immeuble en particulier" : "Aucun immeuble disponible"}
              disabled={immeubleOptions.length === 0}
            />
          </Field>

          <Field label="Bien" htmlFor="agence-dep-bien" hint="Filtré selon l'immeuble.">
            <Select
              id="agence-dep-bien"
              value={form.bienId}
              onChange={(event) => update("bienId", event.target.value)}
              options={bienOptions}
              placeholder={bienOptions.length > 0 ? "Aucun bien en particulier" : "Aucun bien disponible"}
              disabled={bienOptions.length === 0}
            />
          </Field>

          {errors.cible && (
            <p className="flex items-center gap-1.5 text-xs font-label text-red-600 sm:col-span-2" role="alert">
              <i className="ri-error-warning-line text-sm" aria-hidden="true" />
              {errors.cible}
            </p>
          )}

          <Field label="Description" htmlFor="agence-dep-description" className="sm:col-span-2">
            <Textarea
              id="agence-dep-description"
              value={form.description}
              onChange={(event) => update("description", event.target.value)}
              placeholder="Détail de la dépense, prestataire, facture..."
              className="min-h-[96px]"
              maxLength={500}
            />
          </Field>

          <Field
            label="Justificatif"
            htmlFor="agence-dep-justificatif"
            hint="PDF ou image (le fichier reste local)."
            className="sm:col-span-2"
          >
            <Input
              id="agence-dep-justificatif"
              type="file"
              accept="application/pdf,image/*"
              onChange={handleJustificatif}
              className="file:mr-3 file:cursor-pointer file:rounded-md file:border-0 file:bg-background-100 file:px-3 file:py-1.5 file:text-sm file:font-label file:text-foreground-800"
            />
          </Field>
        </div>
      </form>
    </Modal>
  );
}